import { ArrowLeft, Clipboard, FileText, GitBranch, ListChecks } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { getStudentGrowthMe, recordStudentGrowthEvent } from '../api.js'
import { useAuth } from '../auth-context.js'
import {
  STUDENT_PROJECTS,
  buildProjectGrowthTasks,
  buildProjectResumeDraft,
  getStoredStudentPlanProjects,
  pickStudentList,
  pickStudentText,
  saveStoredStudentPlanProjects,
  saveStoredStudentProjectTasks,
} from '../student-projects.js'
import { buildFriendlyProjectDetails, findTermExplanations } from '../student-friendly-explanations.js'
import { getLearningResourcePath } from '../student-learning-resources.js'

const TARGET_PROJECTS = {
  java: [
    {
      id: 'java-springboot-admin',
      title: 'Spring Boot 管理系统',
      audience: '适合 Java 后端入门到实习投递',
      level: '中等',
      resumeLine: '实现用户权限、业务 CRUD、数据统计和后台管理流程，体现 Java Web 后端基础。',
      techStack: ['Java', 'Spring Boot', 'MySQL', 'MyBatis', 'Redis'],
    },
    {
      id: 'java-blog',
      title: '个人博客系统',
      audience: '适合需要完整作品和部署演示的学生',
      level: '入门到中等',
      resumeLine: '实现文章发布、分类标签、评论、后台管理和部署上线。',
      techStack: ['Java', 'Spring Boot', 'MySQL', 'Vue / React'],
    },
    {
      id: 'java-ecommerce-backend',
      title: '电商后端最小版',
      audience: '适合准备后端实习、需要业务复杂度的学生',
      level: '进阶',
      resumeLine: '实现商品、购物车、订单状态和库存管理等核心业务模块。',
      techStack: ['Java', 'Spring Boot', 'MySQL', 'Redis', 'Docker'],
    },
  ],
}

function inferTargetKey(target) {
  const value = String(target || '').toLowerCase()
  if (/java|spring|后端|backend/.test(value)) return 'java'
  return ''
}

function adaptTargetProject(item) {
  return {
    id: item.id,
    title: { zh: item.title, en: item.title },
    audience: { zh: item.audience, en: item.audience },
    level: { zh: item.level, en: item.level },
    resumeLine: { zh: item.resumeLine, en: item.resumeLine },
    skills: { zh: item.techStack, en: item.techStack },
    techStack: { zh: item.techStack, en: item.techStack },
    resumeBullets: { zh: [item.resumeLine], en: [item.resumeLine] },
    type: 'github',
  }
}

function scoreProject(project, target) {
  const haystack = [
    pickStudentText(project.title, 'zh'),
    pickStudentText(project.audience, 'zh'),
    pickStudentText(project.resumeLine, 'zh'),
    ...pickStudentList(project.skills, 'zh'),
    ...pickStudentList(project.techStack, 'zh'),
  ].join(' ').toLowerCase()
  const value = String(target || '').toLowerCase()
  let score = 0
  if (project.type === 'github') score += 3
  if (value && haystack.includes(value)) score += 8
  if (/java|spring|后端|backend/.test(value) && /java|spring|后端|mysql|redis/.test(haystack)) score += 10
  if (/运营|新媒体|内容/.test(value) && /内容|运营|选题|账号/.test(haystack)) score += 10
  if (/数据|分析/.test(value) && /数据|看板|分析|sql|excel/.test(haystack)) score += 10
  return score
}

function uniqueProjects(projects) {
  const seen = new Set()
  return projects.filter((project) => {
    if (seen.has(project.id)) return false
    seen.add(project.id)
    return true
  })
}

function ProjectFriendlyDetails({ friendly, terms }) {
  return (
    <section className="student-friendly-explainer student-project-friendly-explainer">
      <div className="student-friendly-tags">
        {friendly.tags.map((tag) => <span key={tag}>{tag}</span>)}
      </div>
      <div className="student-friendly-rating">
        <span>推荐指数</span>
        <strong>{friendly.rating}</strong>
      </div>
      <dl>
        <div><dt>这个项目能练什么？</dt><dd>{friendly.practice}</dd></div>
        <div><dt>为什么适合当前阶段？</dt><dd>{friendly.fit}</dd></div>
        <div><dt>做完可以写进简历的亮点</dt><dd>{friendly.resume}</dd></div>
        <div><dt>0基础怎么开始？</dt><dd>{friendly.start}</dd></div>
        <div><dt>对应职业方向</dt><dd>{friendly.careers}</dd></div>
        <div><dt>下一步建议</dt><dd>{friendly.next}</dd></div>
      </dl>
      {terms.length ? (
        <div className="student-term-mini-list">
          {terms.map((item) => (
            <p key={item.term}><strong>{item.term}：</strong>{item.explanation}</p>
          ))}
        </div>
      ) : null}
    </section>
  )
}

function StudentProjectRecommendationsPage() {
  const { isLoggedIn } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [growthTarget, setGrowthTarget] = useState(searchParams.get('target') || searchParams.get('direction') || '')
  const [growthPlanId, setGrowthPlanId] = useState('')
  const [selectedDraft, setSelectedDraft] = useState(null)
  const [notice, setNotice] = useState('')
  const targetKey = inferTargetKey(growthTarget)

  useEffect(() => {
    if (growthTarget) return undefined
    if (!isLoggedIn) return undefined
    const controller = new AbortController()
    getStudentGrowthMe(controller.signal)
      .then((payload) => {
        const plan = payload?.plan || null
        setGrowthTarget(plan?.targetDirection || '')
        setGrowthPlanId(plan?.id || '')
      })
      .catch(() => {})
    return () => controller.abort()
  }, [growthTarget, isLoggedIn])

  const projects = useMemo(() => {
    const targetSpecific = (TARGET_PROJECTS[targetKey] || []).map(adaptTargetProject)
    const existing = [...STUDENT_PROJECTS]
      .sort((left, right) => scoreProject(right, growthTarget) - scoreProject(left, growthTarget))
      .slice(0, 8)
    return uniqueProjects([...targetSpecific, ...existing]).slice(0, 8)
  }, [growthTarget, targetKey])

  function selectProject(project) {
    const projectTitle = pickStudentText(project.title, 'zh')
    const current = getStoredStudentPlanProjects()
    const next = current.some((item) => item.id === project.id)
      ? current
      : [...current, { id: project.id, addedAt: new Date().toISOString(), target: growthTarget }]
    saveStoredStudentPlanProjects(next)
    saveStoredStudentProjectTasks(buildProjectGrowthTasks(project).map((title, index) => ({
      id: `project-${project.id}-${index}`,
      title,
      type: 'project',
      status: 'pending',
      projectId: project.id,
      createdAt: new Date().toISOString(),
    })))
    if (isLoggedIn) {
      recordStudentGrowthEvent({
      planId: growthPlanId || undefined,
      eventType: 'project_selected',
      title: `选择项目：${projectTitle}`,
      description: '用户从项目推荐页选择了项目并加入今日任务。',
      metadata: { projectId: project.id, target: growthTarget },
      }).catch(() => {})
    }
    setNotice('已加入今日任务，可回到成长中心继续推进。')
  }

  function writeResume(project) {
    const projectTitle = pickStudentText(project.title, 'zh')
    setSelectedDraft(buildProjectResumeDraft(project))
    selectProject(project)
    if (isLoggedIn) {
      recordStudentGrowthEvent({
      planId: growthPlanId || undefined,
      eventType: 'project_resume_written',
      title: `生成项目简历描述：${projectTitle}`,
      description: '用户为推荐项目生成了简历描述。',
      metadata: { projectId: project.id, target: growthTarget },
      }).catch(() => {})
    }
  }

  return (
    <main className="page-container student-project-rec-page">
      <div className="student-project-rec-topbar">
        <button type="button" className="secondary-button" onClick={() => navigate('/growth')}>
          <ArrowLeft size={16} />
          返回成长中心
        </button>
        <Link className="secondary-button" to={`/more/resume?target=${encodeURIComponent(growthTarget)}`}>
          <FileText size={16} />
          根据目标优化简历
        </Link>
      </div>

      <section className="student-project-rec-hero">
        <span><ListChecks size={17} /> 项目推荐</span>
        <h1>{growthTarget || '当前职业目标'} 的项目准备</h1>
        <p>不只展示技术名词，也说明这个项目能练什么、为什么适合、怎么开始、能不能写进简历。</p>
      </section>

      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      <section className="student-project-rec-grid">
        {projects.map((project) => {
          const title = pickStudentText(project.title, 'zh')
          const stack = pickStudentList(project.techStack, 'zh')
          const skills = stack.length ? stack : pickStudentList(project.skills, 'zh')
          const friendly = buildFriendlyProjectDetails(project, growthTarget)
          const terms = findTermExplanations([title, pickStudentText(project.audience, 'zh'), skills.join(' '), friendly.careers])
          return (
            <article className="student-project-rec-card" key={project.id}>
              <div>
                <span>{pickStudentText(project.level, 'zh') || '中等'}</span>
                <h2>{title}</h2>
                <p>{pickStudentText(project.audience, 'zh')}</p>
              </div>
              <dl>
                <div>
                  <dt>原有技术信息</dt>
                  <dd>{skills.join(' / ') || '按项目方向补齐工具和技术'}</dd>
                </div>
                <div>
                  <dt>可写进简历的原始亮点</dt>
                  <dd>{pickStudentText(project.resumeLine, 'zh')}</dd>
                </div>
              </dl>
              <ProjectFriendlyDetails friendly={friendly} terms={terms} />
              <div className="student-project-rec-actions">
                <Link to={getLearningResourcePath(title)}>
                  <ListChecks size={15} />
                  学习资源
                </Link>
                <Link to="/project-health">
                  <GitBranch size={15} />
                  分析 GitHub 项目
                </Link>
                <button type="button" onClick={() => writeResume(project)}>
                  <Clipboard size={15} />
                  生成简历项目描述
                </button>
                <button type="button" onClick={() => selectProject(project)}>
                  <ListChecks size={15} />
                  加入今日任务
                </button>
              </div>
            </article>
          )
        })}
      </section>

      {selectedDraft ? (
        <section className="student-project-resume-draft">
          <span>简历项目经历文本</span>
          <h2>{selectedDraft.name}</h2>
          <div>
            <strong>项目职责</strong>
            <p>{selectedDraft.role}</p>
          </div>
          <div>
            <strong>技术栈</strong>
            <p>{selectedDraft.techStack.join(' / ') || '按实际项目填写'}</p>
          </div>
          <div>
            <strong>STAR 描述</strong>
            <p>{selectedDraft.star}</p>
          </div>
          <div>
            <strong>可复制简历文本</strong>
            <p>{selectedDraft.resumeText}</p>
          </div>
        </section>
      ) : null}
    </main>
  )
}

export default StudentProjectRecommendationsPage
